<script setup>
import {ref, inject, onBeforeMount, computed} from "vue";

import Button from "primevue/button";
import Dropdown from "primevue/dropdown";
import InputText from "primevue/inputtext";
import ProgressSpinner from "primevue/progressspinner";

import FormFieldError from "@/components/FormFieldError.vue";

import {useToast} from 'primevue/usetoast';

/* Validation */
import ValidationErrors from "@/components/ValidationErrors.vue";

import {useVuelidate} from '@vuelidate/core'
import {serverSide} from '@/lib/validators'
import {useMain} from "@/stores/main";
import FileUpload from "primevue/fileupload";
//import {useTrainees} from "@/stores/trainees";
/* End Validation */

const mainStore = useMain()

const dialogRef = inject('dialogRef', null)

const props = defineProps({
    trainee: {
        type: Object,
    },
    item: {
        type: Object,
    },

})

const emit = defineEmits(['updated'])

const toast = useToast();

const $item = ref(null)

const $loading = ref(true)
const $trainee = ref(null)

const form = ref({
    trainee_documents_type_id: '',
    name: '',
    file: null,
})
const selectedFile = ref(null)
const processing = ref(false)
const setErrors = ref()

const documentType = computed(() => {
    if (!form.value.trainee_documents_type_id) return false
    return mainStore.traineeDocumentsTypes.filter(element => element.id === form.value.trainee_documents_type_id)[0];
})

/**
 * Computed
 */
const errors = computed(() => {
    return setErrors.value ? setErrors.value.errors : null
})

const errorMessage = computed(() => {
    //if (setErrors.value && undefined !== setErrors.value.message) return setErrors.value.message
    if (setErrors.value?.errors.general) return setErrors.value?.errors.general
    if (v$.value.$invalid) return "Une ou plusieurs erreurs sont présentes dans le formulaire.\n" +
        "Merci de rectifier votre saisie."
    return null
})

const canSubmit = computed(() => {
    return v$.value.$invalid
})

const documentTypeOptions = computed(() => {
    if(! $item.value){
        return $trainee.value.availableDocumentTypesToBeAdded
    }

    return mainStore.traineeDocumentsTypes.filter((type) => {
        // multiple, always available to be added
        if(type.multiple) return true

        // not muultiple, filter using existing documents
        return $trainee.value.documents.every((doc) => {
            if(doc.type.id === type.id && $item.value.type.id !== type.id) {
                return false
            }
            return true
        })

    })
})

const closeDialog = () => {
    if (dialogRef.value) dialogRef.value.close($trainee)
}

function uploadHandler(event) {
    selectedFile.value = event.files[0]
    form.value.file = event.files[0] // ✅ Ajoutez ceci
    v$.value.file.$touch()

    if(documentType.value && documentType.value.multiple && !form.value.name) {
        form.value.name = form.value.file.name.substring(0, form.value.file.name.lastIndexOf('.'));
    }
}

const submit = async () => {
    clearErrors()
    const isFormCorrect = await v$.value.$validate()
    if (!isFormCorrect) return

    const data = {
        ...form.value,
        user_id: $trainee.value.id,
    }

    if ($item.value) {
        data.id = $item.value.id
    }

    const updatedItem = await $trainee.value.updateDocument(data, processing, setErrors)

    if (updatedItem) {
        processing.value = true

        emit('updated', $trainee.value, updatedItem)

        processing.value = false

        toast.add({
            severity: 'success',
            summary: 'Effectué.',
            detail: 'Document ' + ($item.value ? 'modifié' : 'ajouté') + ' avec succès',
            life: 3000
        });
        closeDialog()
    }

    await v$.value.$validate()
}

/**
 * Form validation
 */
const rules = {
    name: {serverSide: serverSide({field: 'name', errors})},
    trainee_documents_type_id: {serverSide: serverSide({field: 'trainee_documents_type_id', errors})},
    file: {serverSide: serverSide({field: 'file', errors})},
}

const v$ = useVuelidate(rules, form, {$lazy: true})

const clearErrors = () => {
    return setErrors.value = null
}
/* End form validation */

onBeforeMount(async () => {
    await mainStore.loadTraineeDocumentsTypes(true)
    if (dialogRef.value.data) {
        $item.value = dialogRef.value.data.item
        $trainee.value = dialogRef.value.data.trainee
    } else {
        $item.value = props.item
        $trainee.value = props.trainee
    }

    if ($item.value) {
        form.value = {
            ...form.value,
            name: $item.value.name,
            trainee_documents_type_id: $item.value.trainee_documents_type_id,
        }
    } else {
        if( $trainee.value.availableDocumentTypesToBeAdded.length === 1) {
            form.value.trainee_documents_type_id = $trainee.value.availableDocumentTypesToBeAdded[0].id
        }
    }

    $loading.value = false
})
</script>
<template>
    <div v-if="$loading" class="text-center">
        <ProgressSpinner style="width: 50px; height: 50px" strokeWidth="5"></ProgressSpinner>
    </div>
    <form @submit.prevent="submit" class="flex flex-column gap-4" v-else>
        <div>
            <label for="trainee_documents_type_id" class="p-label">Type de document</label>
            <div class="p-inputgroup">
                <Dropdown
                    id="trainee_documents_type_id"
                    v-model="form.trainee_documents_type_id"
                    optionLabel="label"
                    optionValue="id"
                    :options="documentTypeOptions"
                    type="text"
                    class=""
                    @blur="v$.trainee_documents_type_id.$touch"
                    @focus="clearErrors"
                    :class="{ 'p-invalid': v$.trainee_documents_type_id.$error }"/>
            </div>
            <FormFieldError :errors="v$.trainee_documents_type_id.$error ? v$.trainee_documents_type_id.$errors : [] "/>
        </div>
        <div v-if="documentType && documentType.multiple">
            <label for="name" class="p-label">Nom du document</label>
            <div class="p-inputgroup">
                <InputText
                    id="name"
                    v-model="form.name"
                    type="text"
                    class=""
                    autocomplete="family-name"
                    @blur="v$.name.$touch"
                    @focus="clearErrors"
                    :class="{ 'p-invalid': v$.name.$error }"/>
            </div>
            <FormFieldError :errors="v$.name.$error ? v$.name.$errors : [] "/>
        </div>

        <div>
            <label for="fileUpload" class="p-label mb-2">Document</label>
            <div v-if="selectedFile" class="mb-2">
                <strong>Fichier sélectionné :</strong> {{selectedFile.name}}
            </div>

            <div class="p-inputgroup">
                <FileUpload
                    ref="fileUpload"
                    icon="mdi mdi-upload"
                    mode="basic"
                    name="file"
                    chooseLabel="Sélectionnez un document"
                    accept="image/png, image/jpeg, application/pdf"
                    :maxFileSize="10000000"
                    auto
                    withCredentials
                    custom-upload
                    @click="clearErrors"
                    @uploader="uploadHandler"
                />
            </div>
            <div><FormFieldError :errors="v$.file.$error ? v$.file.$errors : [] "/></div>
            <small>Un document PDF de préférence, une image JPEG ou PNG. 10mo maximum.</small>
        </div>

        <ValidationErrors class="mb-0 w-full" :error="errorMessage"/>

        <div class="flex justify-content-between align-items-center mt-0">
            <Button
                label="Annuler"
                class="p-0"
                link
                @click="closeDialog"
                severity="secondary"
            >

            </Button>

            <Button type="submit" icon="mdi mdi-login" :loading="processing" :disabled="canSubmit"
                    label="Enregistrer"/>
        </div>

    </form>
</template>
